import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Shredder, ShieldAlert, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Cleanup Duplicate Records card — Super-Admin only.
 *  Two-step UX: Preview (dry-run) → confirm dialog → Run.
 *  Every run creates a full MongoDB snapshot first, then rewrites FKs and
 *  deletes the newer duplicates so historical child records stay intact. */
export default function DedupeCard() {
  const [targets, setTargets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/dedupe/targets`, { withCredentials: true })
      .then(r => setTargets(r.data || []))
      .catch(() => {});
  }, []);

  const runPreview = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/dedupe/preview`, {}, { withCredentials: true });
      setPreview(data);
      const total = data?.totals?.duplicate_rows || 0;
      if (total === 0) toast.success('No duplicates found — database is clean');
      else toast.warning(`${total} duplicate row(s) found across ${data.totals.duplicate_groups} group(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    setRunning(true); setConfirmOpen(false);
    try {
      const { data } = await axios.post(`${API_URL}/api/dedupe/run`, {}, { withCredentials: true });
      setResult(data);
      toast.success(`Cleaned ${data.totals.duplicate_rows} duplicate row(s)`);
      setPreview(null); // force fresh preview next time
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setRunning(false);
    }
  };

  const hasDuplicates = (preview?.totals?.duplicate_rows || 0) > 0;

  return (
    <Card className="bg-slate-950/40 border-amber-900" data-testid="dedupe-card">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Shredder className="h-5 w-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">Cleanup Duplicate Records</h2>
          <span className="ml-2 text-[10px] font-medium bg-amber-500/20 text-amber-300 rounded px-2 py-0.5">
            One-shot maintenance
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Scans customers (by CNIC), users (username), inventory (IMEI / serial), branches, investors
          and installment plans for duplicates. The <strong>oldest record wins</strong> — related
          installments, payments and audit trail get rewritten to point at the keeper before the
          duplicates are deleted. A full MongoDB backup is taken before any cleanup starts.
          Going forward, unique indexes at the database level block duplicate CNIC / IMEI / usernames
          before they ever reach a table.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button onClick={runPreview} disabled={loading}
            data-testid="dedupe-preview-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</>
                     : <><Shredder className="h-4 w-4 mr-2" /> Scan for duplicates</>}
          </Button>
          {hasDuplicates && (
            <Button onClick={() => setConfirmOpen(true)} disabled={running}
              data-testid="dedupe-commit-btn"
              className="bg-red-600 hover:bg-red-700 text-white">
              {running
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cleaning…</>
                : <><ShieldAlert className="h-4 w-4 mr-2" /> Clean {preview.totals.duplicate_rows} duplicate row(s)</>}
            </Button>
          )}
        </div>

        {/* Preview / result table */}
        {(preview || result) && (
          <div className="border border-slate-800 rounded-sm bg-slate-900/40 overflow-hidden"
               data-testid="dedupe-report">
            <div className="text-xs text-slate-500 px-3 py-2 border-b border-slate-800">
              {result
                ? <span className="text-emerald-400 font-medium flex items-center">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Cleanup completed at {new Date(result.run_at).toLocaleString('en-GB')} —
                    backup at <span className="font-mono ml-1">{result.backup_path}</span>
                  </span>
                : <span>Dry-run preview at {new Date(preview.run_at).toLocaleString('en-GB')}</span>}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-900">
                <tr className="text-left text-slate-400">
                  <th className="px-3 py-1.5">Target</th>
                  <th className="px-3 py-1.5 text-right">Duplicate groups</th>
                  <th className="px-3 py-1.5 text-right">Rows to remove</th>
                  <th className="px-3 py-1.5">Example</th>
                </tr>
              </thead>
              <tbody>
                {(result || preview).targets.map((t) => (
                  <tr key={t.target} className="border-t border-slate-800"
                      data-testid={`dedupe-row-${t.target}`}>
                    <td className="px-3 py-1.5 text-slate-300">{t.label}</td>
                    <td className="px-3 py-1.5 text-right text-amber-400">{t.duplicate_groups}</td>
                    <td className="px-3 py-1.5 text-right text-red-400">{t.duplicate_rows}</td>
                    <td className="px-3 py-1.5 text-slate-500">
                      {t.sample_groups?.[0]
                        ? <>
                            <span className="font-mono text-slate-300">{t.sample_groups[0].key}</span>
                            {' — keep '}
                            <span className="text-emerald-400">{t.sample_groups[0].keeper.name || '—'}</span>
                            {t.sample_groups[0].duplicates.length > 0 &&
                              <>, drop {t.sample_groups[0].duplicates.length} newer copy(ies)</>}
                          </>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-800 bg-slate-900/60 text-slate-300 font-medium">
                  <td className="px-3 py-1.5">Total</td>
                  <td className="px-3 py-1.5 text-right">{(result || preview).totals.duplicate_groups}</td>
                  <td className="px-3 py-1.5 text-right">{(result || preview).totals.duplicate_rows}</td>
                  <td className="px-3 py-1.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center">
              <ShieldAlert className="h-5 w-5 mr-2 text-red-500" />
              Confirm duplicate cleanup
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete the newer duplicate rows and rewire their related
              records (installments, payments, audit trail…) to the oldest match. A full
              MongoDB backup is taken first — nothing is lost.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Groups affected:</span>{' '}
              <span className="text-amber-400 font-semibold">{preview?.totals?.duplicate_groups}</span></div>
            <div><span className="text-slate-500">Rows to remove:</span>{' '}
              <span className="text-red-400 font-semibold">{preview?.totals?.duplicate_rows}</span></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}
              className="text-slate-300">Cancel</Button>
            <Button onClick={commit}
              data-testid="dedupe-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white">
              Yes, clean duplicates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
